import {
  faCalendar,
  faPlus,
  faPlusCircle,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import CustomButton from "../components/CustomButton";
import PageLayout from "../components/PageLayout";
import SearchIcon from "../../src/assests/SearchIcon.svg";
import axiosInstance from "../utils/axiosInstance";
import CreateAgentModel from "../components/agentComponents/CreateAgentModel";
import { User } from "../providers/AuthProvider";
import { agentHeading } from "../components/agentComponents/agentTableData";
import DeleteAgentModel from "../components/agentComponents/DeleteAgentModel";
import PaginationMenu from "../components/pagination/paginationMenu";
import formQueryUrl from "../utils/formQueryUrl";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import clsx from "clsx";
import SkeltonTableRow from "../components/skelton/SkeltonTableRow";
import UserRow from "../components/agentComponents/AgentRow";
import NoItem from "../components/NoItem";

const Agent = () => {
  const [search, setSearch] = useDebounceValue<string>("", 1000);
  const [model, setModel] = useState<User | boolean>(false);
  const [isDelete, setIsDelete] = useState<number | null>(null);
  const [users, setUsers] = useState<{ totalCount: number; data: User[] }>({
    totalCount: 0,
    data: [],
  });
  const [pageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const { onToggleRangePicker } = useRangePicker();
  const [menuOpen, setMenuOpen] = useState<number | null>(null);
  const isModalOpen = Boolean(model || isDelete);

  // ---------------------------- [ USER DATA ] ----------------------------------------
  useEffect(() => {
    if (isModalOpen) return;
    const FindUsers = async () => {
      setLoading(true);
      try {
        const filtersArray = [
          search && { search: search },
          dateRange?.startDate !== null && { startDate: dateRange?.startDate },
          dateRange?.endDate !== null && { endDate: dateRange?.endDate },
        ];
        const res = await axiosInstance.get(
          `/user?page=${page}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setUsers(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    FindUsers();
  }, [
    isModalOpen,
    search,
    page,
    pageSize,
    dateRange?.startDate,
    dateRange?.endDate,
  ]);

  // ---------------------------------- [ OPEN AND CLOSE MODEL ] -----------------------------
  const ToggleModel = () => {
    setModel((prev) => (typeof prev === "boolean" ? !prev : false));
  };

  const ToggleDeleteModel = (id?: number) => {
    setIsDelete(id ? id : null);
  };

  // --------------------------------------- [ CALL TO ACTIONS ] -----------------------------
  const tableAction = [
    {
      name: "Edit Agent",
      icon: faPlusCircle,
      actionHandler: (data: User) => setModel(data),
    },
    {
      name: "Delete Agent",
      icon: faTrashCan,
      actionHandler: (data: User) => ToggleDeleteModel(data?.id),
    },
  ];

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setPage(page);
  };

  const handleMenuToggle = (rowIndex: number) => {
    setMenuOpen((prevIndex) => (prevIndex === rowIndex ? null : rowIndex));
  };

  return (
    <PageLayout
      pageTitle="Agents"
      primaryAction={
        <>
          <div className="relative">
            <DateRangePicker onChange={(e) => setDateRange(e)}>
              <div
                onClick={onToggleRangePicker}
                className="px-6 py-1 rounded-full appearance-none text-[16px] flex items-center justify-center h-[50px] bg-surface-elevated text-light-gray border border-edge hover:border-indigo-400/40 hover:text-text-primary transition-all duration-300 outline-none cursor-pointer"
              >
                <FontAwesomeIcon icon={faCalendar} />
              </div>
            </DateRangePicker>
          </div>
          <CustomButton
            className="!rounded-full !font-dmSans !text-[16px] !font-bold ml-4 max-sm:ml-0"
            variant="primary"
            renderLeftIcon={
              <FontAwesomeIcon
                icon={faPlus}
                className="bg-white/20 text-white text-[16px] rounded-full outline-none p-1"
              />
            }
            onClick={ToggleModel}
          >
            Add Agent
          </CustomButton>
        </>
      }
    >
      {isDelete && (
        <DeleteAgentModel ToggleDeleteModel={ToggleDeleteModel} id={isDelete} />
      )}
      {model && <CreateAgentModel ToggleModel={ToggleModel} isEdit={model} />}
      <div className="bg-surface px-8 max-sm:px-4 border mt-8 border-edge rounded-2xl shadow-card py-6">
        <div className="mb-4 flex justify-between items-center max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
          <div>
            <h1 className="font-bold leading-[31px] bg-gradient-heading bg-clip-text text-transparent text-[22px] ">
              Agent List
            </h1>
            <p className="text-light-gray font-medium text-[16px]">
              All agents
            </p>
          </div>
          <div className="ml-auto max-sm:ml-0 rounded-3xl flex bg-input-bg text-light-gray items-center border border-input-border focus-within:border-indigo-400/40 focus-within:ring-1 focus-within:ring-indigo-400/30 transition-all duration-200 py-2 px-4">
            <div className="ml-1 mr-3 flex items-center text-label-muted">
              <img src={SearchIcon} className="w-3 h-3" alt="" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="flex-grow font-dmSans text-sm text-text-primary placeholder:text-label-muted bg-inherit focus:outline-none"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3">
          <div className="max-lg:overflow-x-auto">
            <table className={clsx("table-auto h-max w-full text-left max-lg:min-w-[820px]")}>
              <thead>
                <tr className="font-medium border-b border-edge-faint">
                  {agentHeading.map((data, index) => (
                    <th
                      key={index}
                      className="uppercase text-light-gray font-medium text-[10px] py-2 tracking-wider"
                    >
                      {data.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeltonTableRow />
                ) : (
                  users?.data?.map((row, rowIndex: number) => {
                    return (
                      <UserRow
                        key={rowIndex}
                        rowIndex={rowIndex}
                        row={row}
                        handleMenuToggle={handleMenuToggle}
                        menuOpen={menuOpen}
                        tableAction={tableAction}
                        noSpaceAction
                      />
                    );
                  })
                )}
              </tbody>
            </table>
            {!loading && !users?.totalCount && (
              <NoItem
                title="No Users Found"
                desc="There's nothing to see for users"
              />
            )}
          </div>
          <PaginationMenu
            totalCount={users?.totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={OnChangeCurrentPage}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default Agent;
